import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { ReactComponent as EditIcon } from '@/assets/icons/ri-edit-2-line.svg';

import BreadcrumbLayout from '@/components/common/CustomLayout/BreadcrumbLayout';
import Table from '@/components/common/Table';
import Button from '@/components/common/Button';
import Input from '@/components/common/Input';
import useUserVenueSelectFilter from '@/hooks/useUserVenueSelectFilter';
import {
	TOTAL_OPTION_FITLER,
	OPTION_FILTER_STYLE,
	FILTER_KEYS,
	FILTER,
	HEADERS,
} from './constant';

import { getAllClubs } from '@/store/clubs/action';
import { getUserVenues } from '@/store/user-info/action';

import useDebounce from '@/hooks/useDebounce';
import { RootState, useAppDispatch } from '@/store';
import {GENERAL_STATUS_STYLE} from "@/constant/status";

const SORT: TableSort = {
	type: 'SERVER',
	handler: () => {},
	customSort: {
		NAME: (v1, v2) => v2.name.localeCompare(v1.name),
	},
};

const renderHeaderSection = (setSearchClub) => (
	<div className="flex justify-between items-center gap-8 p-0">
		<p className="font-bold text-2xl text-black-primary">Clubs</p>
		<div className="flex items-center gap-4">
			<Input
				useReactForm={false}
				name="searchClub"
				type="text"
				placeholder="Search for Clubs"
				onChange={(e) => setSearchClub(e.target.value)}
			/>
		</div>
	</div>
);

const OptionFilter = ({
	index,
	status,
	isActive,
	onClick,
	total,
}: OptionFilterParam) => {
	const { id, name } = status;
	const selectedStyle = OPTION_FILTER_STYLE[`${isActive}`];

	const rightBorderStyle =
		index === TOTAL_OPTION_FITLER - 1 ? '' : 'border-r-0';

	return (
		<div
			role="button"
			tabIndex={0}
			onKeyDown={() => onClick(id)}
			onClick={() => onClick(id)}
			className={`flex border border-grey-soft py-2 px-4 cursor-pointer ${rightBorderStyle} ${selectedStyle.button}`}
		>
			<span className="mr-2">{name}</span>
			<div className={`px-2 rounded-xl ${selectedStyle.pill}`}>{total}</div>
		</div>
	);
};

const renderFilterSection = ({
	summary,
	activeStatus,
	onClick,
}: ClubRenderFilterSectionParam) => (
	<div className="flex items-center border border-grey-soft border-b-0">
		<div className="flex flex-row p-4">
			{FILTER_KEYS.map((filterKey, index) => (
				<OptionFilter
					key={FILTER[filterKey].id}
					index={index}
					status={FILTER[filterKey]}
					isActive={activeStatus === FILTER[filterKey].id}
					onClick={onClick}
					total={summary ? Number(summary[filterKey.toLocaleLowerCase()]) : 0}
				/>
			))}
		</div>
		<div className="border-r border-r-stroke-subtle h-6 my-auto mr-4"/>
	</div>
);

const renderCustomComponent = ({ onEditClick }) => ({
	NAME: ({ name, logoUrl }: ClubNameSectionParam) => (
		<div className="flex flex-row">
			<img className="mr-2 object-cover h-[50px] w-[50px]" src={logoUrl} alt="" />
			<span className="font-semibold">{name}</span>
		</div>
	),
	STATUS: (status) => (
		<div className={`p-1 text-center font-medium rounded ${GENERAL_STATUS_STYLE[status]}`}>{status}</div>
	),
	CREATED: (createdAt) => {
		const date = new Date(createdAt)
		return (
			<div className="flex flex-col p-0 gap-3">
				<div className="flex flex-col items-start p-0 gap-1">
					<p>
						{date.toLocaleDateString('id-ID', { year: 'numeric', month: 'short', day: '2-digit' })}, {date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
					</p>
				</div>
			</div>
		);
	},
	ACTION: (id) => (
		<div className="flex">
			<Button
				className="border border-grey-line-subtle px-4 py-2 mr-2"
				type="button"
				variant="basic"
				onClick={() => onEditClick(id)}
			>
				<EditIcon />
				<span className="ml-2">Edit</span>
			</Button>
		</div>
	),
});


const Clubs = () => {
	const dispatch = useAppDispatch();
	const {
		items: clubs,
		meta,
		summary,
	} = useSelector((state: RootState) => state.club.state);

	const [searchParams, setSearchParams] = useSearchParams();
	const [
		selectedVenue,
		venueOptions,
		handleChangeVenue,
	] = useUserVenueSelectFilter()
	const searchClubParam = searchParams.get('searchClub');
	const activeStatus = searchParams.get('status') || 'ALL';

	const navigate = useNavigate();

	const clubTableBody = clubs.map((v) => ({
		id: v.id,
		value: {
			NAME:  {
				name: v.name,
				logoUrl: v.logo_url
			},
			ADMIN:  v.admin.name,
			STATUS: v.status,
			CREATED: v.created_at,
			ACTION: v.id,
		},
	})) as TableBodyData[];

	const onEditClick = (id) => {
		navigate(`/dashboard/customer/clubs/edit/${id}`);
	};

	const body: TableBody = {
		data: clubTableBody,
		component: renderCustomComponent({ onEditClick }),
	};

	const [searchClub, setSearchClub] = useState(searchClubParam);

	const debounceSearchClub = useDebounce(searchClub, 500);

	useEffect(() => {
		if (debounceSearchClub) {
			setSearchParams({
				searchClub: debounceSearchClub,
				status: activeStatus,
			});
		}
	}, [debounceSearchClub]);

	useEffect(() => {
		dispatch(getUserVenues({}))
		const finalStatus =
			activeStatus === 'ALL' || !activeStatus ? '' : activeStatus;
		dispatch(getAllClubs({ status: finalStatus, search: searchClub || '' }));
	}, [searchParams]);

	const handleChangeStatus = (status: string) => {
		setSearchParams({
			searchClub: debounceSearchClub,
			status,
		});
	};

	const tableMeta: TableSummarizeMeta = {
		startItem: meta ? meta.page * meta.size + 1 : 0,
		endItem: meta ? meta.page * meta.size + meta.size : 0,
		totalItem: summary ? summary.all : 0,
		lastPage: summary ? summary.all / 10 : 0,
		pageNumber: 1,
	};

	return (
		<BreadcrumbLayout>
			<div className="flex flex-col justify-between gap-8 p-0">
				{renderHeaderSection(setSearchClub)}

				<div className="">
					{renderFilterSection({
						summary,
						activeStatus,
						onClick: handleChangeStatus,
						venueOptions,
						selectedVenue,
						handleChangeVenue
					})}
					<Table headers={HEADERS} body={body} sort={SORT} meta={tableMeta} />
				</div>
			</div>
		</BreadcrumbLayout>
	);
};

export default Clubs;
